import axios from '../axiosConfig';
import { API_BASE_URL } from '../config';

const baseURL = API_BASE_URL;

/**
 * Comprehensive search across all entities
 * @param {string} query - Search query (minimum 3 characters)
 * @param {number} limit - Maximum number of results per entity type (default: 20, max: 100)
 * @param {number} offset - Number of results to skip for pagination (default: 0)
 * @returns {Promise} Search results grouped by entity type
 */
const comprehensiveSearch = async (query, limit = 20, offset = 0) => {
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };
  
  if (!axios.defaults.headers.common["Authorization"]) {
    headers['Authorization'] = `Bearer ${sessionStorage.getItem("authToken")}`;
  }

  try {
    const response = await axios.get(`/search/search?q=${encodeURIComponent(query)}&limit=${limit}&offset=${offset}`, {
      headers: headers
    });
    return response;
  } catch (error) {
    console.error('Error in comprehensive search:', error);
    return error;
  }
};

/**
 * Search products specifically
 * @param {string} query - Search query (minimum 3 characters)
 * @param {number} limit - Maximum number of results (default: 20, max: 100)
 * @param {number} offset - Number of results to skip (default: 0)
 * @returns {Promise} Product search results
 */
const searchProducts = async (query, limit = 20, offset = 0) => {
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };
  
  if (!axios.defaults.headers.common["Authorization"]) {
    headers['Authorization'] = `Bearer ${sessionStorage.getItem("authToken")}`;
  }

  try {
    const response = await axios.get(`/search/products?q=${encodeURIComponent(query)}&limit=${limit}&offset=${offset}`, {
      headers: headers
    });
    return response;
  } catch (error) {
    console.error('Error in product search:', error);
    return error;
  }
};

/**
 * Search events specifically
 * @param {string} query - Search query (minimum 3 characters)
 * @param {number} limit - Maximum number of results (default: 20, max: 100)
 * @param {number} offset - Number of results to skip (default: 0)
 * @returns {Promise} Event search results
 */
const searchEvents = async (query, limit = 20, offset = 0) => {
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };
  
  if (!axios.defaults.headers.common["Authorization"]) {
    headers['Authorization'] = `Bearer ${sessionStorage.getItem("authToken")}`;
  }

  try {
    const response = await axios.get(`/search/events?q=${encodeURIComponent(query)}&limit=${limit}&offset=${offset}`, {
      headers: headers
    });
    return response;
  } catch (error) {
    console.error('Error in event search:', error);
    return error;
  }
};

/**
 * Search stores specifically
 * @param {string} query - Search query (minimum 3 characters)
 * @param {number} limit - Maximum number of results (default: 20, max: 100)
 * @param {number} offset - Number of results to skip (default: 0)
 * @returns {Promise} Store search results
 */
const searchStores = async (query, limit = 20, offset = 0) => {
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };
  
  if (!axios.defaults.headers.common["Authorization"]) {
    headers['Authorization'] = `Bearer ${sessionStorage.getItem("authToken")}`;
  }

  try {
    const response = await axios.get(`/search/stores?q=${encodeURIComponent(query)}&limit=${limit}&offset=${offset}`, {
      headers: headers
    });
    return response;
  } catch (error) {
    console.error('Error in store search:', error);
    return error;
  }
};

/**
 * Search OEM companies (superadmin only)
 * @param {string} query - Search query (minimum 3 characters)
 * @param {number} limit - Maximum number of results (default: 20, max: 100)
 * @param {number} offset - Number of results to skip (default: 0)
 * @returns {Promise} OEM company search results
 */
const searchOEMCompanies = async (query, limit = 20, offset = 0) => {
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };
  
  if (!axios.defaults.headers.common["Authorization"]) {
    headers['Authorization'] = `Bearer ${sessionStorage.getItem("authToken")}`;
  }

  try {
    const response = await axios.get(`/search/oem-companies?q=${encodeURIComponent(query)}&limit=${limit}&offset=${offset}`, {
      headers: headers
    });
    return response;
  } catch (error) {
    console.error('Error in OEM company search:', error);
    return error;
  }
};

/**
 * Search users (superadmin only)
 * @param {string} query - Search query (minimum 3 characters)
 * @param {number} limit - Maximum number of results (default: 20, max: 100)
 * @param {number} offset - Number of results to skip (default: 0)
 * @returns {Promise} User search results
 */
const searchUsers = async (query, limit = 20, offset = 0) => {
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };
  
  if (!axios.defaults.headers.common["Authorization"]) {
    headers['Authorization'] = `Bearer ${sessionStorage.getItem("authToken")}`;
  }

  try {
    const response = await axios.get(`/search/users?q=${encodeURIComponent(query)}&limit=${limit}&offset=${offset}`, {
      headers: headers
    });
    return response;
  } catch (error) {
    console.error('Error in user search:', error);
    return error;
  }
};

/**
 * Search employees (superadmin and employee3 only)
 * @param {string} query - Search query (minimum 3 characters)
 * @param {number} limit - Maximum number of results (default: 20, max: 100)
 * @param {number} offset - Number of results to skip (default: 0)
 * @returns {Promise} Employee search results
 */
const searchEmployees = async (query, limit = 20, offset = 0) => {
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };
  
  if (!axios.defaults.headers.common["Authorization"]) {
    headers['Authorization'] = `Bearer ${sessionStorage.getItem("authToken")}`;
  }

  try {
    const response = await axios.get(`/search/employees?q=${encodeURIComponent(query)}&limit=${limit}&offset=${offset}`, {
      headers: headers
    });
    return response;
  } catch (error) {
    console.error('Error in employee search:', error);
    return error;
  }
};

/**
 * Get search suggestions
 * @param {string} query - Partial search query (minimum 2 characters)
 * @param {number} limit - Maximum number of suggestions (default: 10, max: 50)
 * @returns {Promise} Search suggestions
 */
const getSearchSuggestions = async (query, limit = 10) => {
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };
  
  if (!axios.defaults.headers.common["Authorization"]) {
    headers['Authorization'] = `Bearer ${sessionStorage.getItem("authToken")}`;
  }

  try {
    const response = await axios.get(`/search/suggestions?q=${encodeURIComponent(query)}&limit=${limit}`, {
      headers: headers
    });
    return response;
  } catch (error) {
    console.error('Error getting search suggestions:', error);
    return error;
  }
};

/**
 * Get search statistics and available entity types
 * @returns {Promise} Search statistics and available entities
 */
const getSearchStats = async () => {
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };
  
  if (!axios.defaults.headers.common["Authorization"]) {
    headers['Authorization'] = `Bearer ${sessionStorage.getItem("authToken")}`;
  }

  try {
    const response = await axios.get('/search/stats', {
      headers: headers
    });
    return response;
  } catch (error) {
    console.error('Error getting search stats:', error);
    return error;
  }
};

export {
  comprehensiveSearch,
  searchProducts,
  searchEvents,
  searchStores,
  searchOEMCompanies,
  searchUsers,
  searchEmployees,
  getSearchSuggestions,
  getSearchStats,
  baseURL
}; 